import { useContext, useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { Download } from "lucide-react";
import { ReactComponent as EllipseIcon } from "icons/Menu/Ellipse.svg";
import { DocumentType } from "protogen/documents_pb";
import { Family } from "protogen/advisors_service_pb";
import { CopyDocumentRequest } from "protogen/documents_service_pb";
import { PhoneCall, PhoneCall_State } from "protogen/conversation_pb";
import { LiveTranscriptionsRequest } from "protogen/phone_service_pb";
import { AccountStub } from "protogen/common_pb";
import { useListAdvisorFamilies } from "services/advisor";
import { useDeepgramLiveTranscriptions, useGetPhoneCall } from "services/phone";
import { useCopyDocument } from "services/documents";
import PhoneCallDetailsPanel from "components/details-page/PhoneCallDetailsPanel";
import useIsVisible from "components/hooks/useIsVisible";
import { SearchContext } from "components/context/SearchContextProvider";
import GridPage from "components/layout/GridPage";
import {
  commaSeparatedEnglishList,
  formatDateForFilename,
  formatUSPhoneNumber,
  getFormattedDuration,
} from "common/utils";
import Duration from "components/common/Duration";
import useIsMobile from "components/hooks/useIsMobile";
import Breadcrumbs from "components/common/Breadcrumbs";
import DateDisplay from "components/common/DateDisplay";
import PhoneCallDropDown from "components/phone/PhoneCallDropDown";
import CopyToButton from "components/documents/CopyToButton";
import { createCopyToOptions } from "components/phone/utils";
import BookmarkToTaskDialog from "components/tasks/BookmarkToTaskDialog";
import { fromPhoneCallNotes } from "components/tasks/BookmarkToTaskDialog";
import TwoPanelGrid, { Panel } from "components/details-page/TwoPanelGrid";
import Loading from "components/common/Loading";
import PhoneCallActivityPanel, {
  ActivityEntry,
  aggregateEntries,
} from "components/details-page/PhoneCallActivityPanel";

const useTranscript = (phoneCall: PhoneCall) => {
  const { request } = useDeepgramLiveTranscriptions();

  const getTranscript = async () => {
    const resp = await request(
      new LiveTranscriptionsRequest({
        callRef: phoneCall.ref,
      }),
    );
    if (!resp) {
      throw new Error("Failed to download transcript");
    }
    return aggregateEntries(resp.segments.filter((s) => s.stable));
  };

  const transcriptToString = (
    transcript: ActivityEntry[],
    trackToUser: Record<string, AccountStub>,
  ) => {
    return (
      transcript
        .map((s) => `${trackToUser[s.track].displayName}: ${s.text}`)
        .join("\n") + "\n"
    );
  };

  const copyTranscript = async (trackToUser: Record<string, AccountStub>) => {
    const transcript = await getTranscript();
    await navigator.clipboard.writeText(
      transcriptToString(transcript, trackToUser),
    );
  };

  const downloadTranscript = async (
    trackToUser: Record<string, AccountStub>,
  ) => {
    const transcript = await getTranscript();
    const blob = new Blob([transcriptToString(transcript, trackToUser)], {
      type: "text/plain;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Call-${formatDateForFilename(
        new Date(Number(phoneCall.creationSec) * 1000),
      )}.txt`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return {
    copyTranscript,
    downloadTranscript,
  };
};

const DownloadTranscript = ({
  phoneCall,
  trackToUser,
}: {
  phoneCall: PhoneCall;
  trackToUser: Record<string, AccountStub>;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { downloadTranscript } = useTranscript(phoneCall);

  return (
    <>
      <IconButton
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label="download options"
      >
        <Download />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            downloadTranscript(trackToUser);
          }}
        >
          Download
        </MenuItem>
      </Menu>
    </>
  );
};

const CopyTranscript = ({
  options,
  isOpen,
  setIsOpen,
  onCopyToHandler,
}: {
  phoneCall: PhoneCall;
  trackToUser: Record<string, AccountStub>;
  options: JSX.Element[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onCopyToHandler: () => void;
}) => {
  return (
    <>
      <CopyToButton
        menuOptions={options}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onClickHandler={onCopyToHandler}
      ></CopyToButton>
    </>
  );
};

export default () => {
  const navigate = useNavigate();
  const location = useLocation();
  let params = useParams();
  const isMobile = useIsMobile();
  const queryParams = new URLSearchParams(location.search);
  let segmentRefs: string[] = [];
  if (queryParams.get("segmentRefs")) {
    segmentRefs = queryParams.get("segmentRefs")!.split(",");
  }
  const { hash } = useLocation();
  const defaultPanel = hash.match(/#details/) ? Panel.Left : Panel.Right;
  const { isVisible } = useIsVisible({});
  const [copyPopperOpen, setCopyPopperOpen] = useState(false);
  const [noteCopyToOptions, setNoteCopyToOptions] = useState<JSX.Element[]>([]);
  const [transcriptCopyToOptions, setTranscriptCopyToOptions] = useState<
    JSX.Element[]
  >([]);
  const [copyToFamily, setCopyToFamily] = useState<Family | undefined>(
    undefined,
  );
  const [families, setFamilies] = useState<Family[]>([]);
  const [entityCopyType, setEntityCopyType] = useState<"note" | "transcript">(
    "note",
  );
  const { setPhoneCall } = useContext(SearchContext);
  const { request: copyDocumentRequest } = useCopyDocument();
  const { request: familiesRequest } = useListAdvisorFamilies((r) => {
    setFamilies(r?.families || []);
  });
  const { data, request, errorCode } = useGetPhoneCall((r) => {
    setPhoneCall(r.phoneCall);
  });
  const callRef = params.callRef!;

  useEffect(() => {
    request({ callRef: callRef });
    const intervalId = setInterval(async () => {
      if (isVisible) {
        request({ callRef: callRef });
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [callRef, isVisible]);
  useEffect(() => {
    familiesRequest();
  }, []);

  const { copyTranscript } = useTranscript(data?.phoneCall);
  useEffect(() => {
    setNoteCopyToOptions(
      createCopyToOptions(data?.family, families, setCopyToFamily, () => {
        setCopyPopperOpen(false);
      }),
    );
    setTranscriptCopyToOptions(
      createCopyToOptions(
        data?.family,
        families,
        setCopyToFamily,
        () => {
          setCopyPopperOpen(false);
        },
        true,
        () => {
          copyTranscript(trackToUser);
          setCopyPopperOpen(false);
        },
      ),
    );
  }, [families, data]);

  const copyDocument = async (
    fromEntityType: string,
    fromEntityRef: string,
    toEntityRef: string,
    creationSourceName: string,
  ) => {
    const result = await copyDocumentRequest(
      new CopyDocumentRequest({
        fromDocumentType: DocumentType.CALL,
        fromEntityType: fromEntityType,
        fromEntityRef: fromEntityRef,
        toDocumentType: DocumentType.TASK_BLOCK,
        toEntityType: "task-block",
        toEntityRef: toEntityRef,
        creationSourceName: creationSourceName,
        title: title,
        replaceTitle: true,
      }),
    );
    return result;
  };

  const onBookmarkHandler = async (tasRefs: string[]) => {
    if (tasRefs.length < 1) return;
    const entityRef = tasRefs[0];
    const result =
      entityCopyType === "note"
        ? await copyDocument(
            "phonecall-note",
            callNotes.entityRef,
            entityRef,
            "phonecall-note",
          )
        : await copyDocument(
            "phonecall-transcript",
            callRef,
            entityRef,
            "phonecall-transcript",
          );
    setCopyToFamily(undefined);
    navigate(
      `/tasks/${encodeURIComponent(result?.document?.ref || "")}?created=1`,
    );
  };

  if (errorCode === 404) {
    // Only happens when you delete a draft without other threads.
    return (
      <GridPage>
        <Alert severity="warning">
          <AlertTitle>Phone Call Not Found</AlertTitle>
          No phone call with the given ID was found.
        </Alert>
      </GridPage>
    );
  }
  if (!data) {
    return <Loading />;
  }
  const {
    phoneCall,
    caller,
    recipients,
    callNotes,
    familyRef,
    delegationTaskRef,
    delegationTaskTitle,
  } = data;
  const trackToUser: Record<string, AccountStub> = {
    inbound: caller,
    outbound: recipients[0],
  };

  const isDelegationCall = !!delegationTaskRef;
  // formatUSPhoneNumber returns the formatted number if valid, otherwise the input unchanged
  const title = caller.isAdvisor
    ? `Call with ${commaSeparatedEnglishList(
        recipients.map((r) => formatUSPhoneNumber(r.firstName)).sort(),
      )}`
    : `Call from ${formatUSPhoneNumber(caller.firstName)}`;
  const mainHeader = (
    <Box
      display={"flex"}
      flexDirection={"row"}
      justifyContent={"space-between"}
      alignItems={"center"}
    >
      <Box>
        {isDelegationCall && delegationTaskRef && (
          <Breadcrumbs
            breadcrumbs={[
              {
                name: delegationTaskTitle || "Subtask",
                link: `/tasks/subtasks/${encodeURIComponent(
                  delegationTaskRef,
                )}`,
              },
            ]}
          />
        )}
        {!isDelegationCall && data?.family?.ref && (
          <Breadcrumbs
            breadcrumbs={[
              {
                name: data.family.name,
                link: `/families/${encodeURIComponent(data.family.ref)}`,
              },
            ]}
          />
        )}
        <Typography
          variant="h1Serif"
          sx={{
            margin: "4px 0",
          }}
        >
          {title}
        </Typography>
        <Box
          display={"flex"}
          alignItems={"center"}
          flexDirection={"row"}
          gap={"8px"}
          sx={{
            "svg circle": { fill: "#B3AFB6" },
          }}
        >
          <DateDisplay
            variant="body"
            date={new Date(Number(phoneCall.creationSec) * 1000)}
          />
          {!isMobile ? <EllipseIcon height={4} width={4} /> : null}
          <Typography variant={"body"}>
            {phoneCall.state === PhoneCall_State.ACTIVE ? (
              <Duration
                startTime={new Date(Number(phoneCall.creationSec) * 1000)}
              />
            ) : (
              getFormattedDuration(
                Number(phoneCall.lastUpdatedSec),
                Number(phoneCall.creationSec),
              )
            )}
          </Typography>
        </Box>
      </Box>
      <PhoneCallDropDown
        phoneCall={phoneCall}
        familyRef={familyRef}
        refresh={async () => {
          await request({ callRef: callRef });
        }}
      />
    </Box>
  );
  const transcriptHeader = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent:
          phoneCall.state === PhoneCall_State.COMPLETED && !isMobile
            ? "space-between"
            : "start",
        padding: "16px 0",
        gap: "12px",
        borderBottom: "1px solid #975d331a",
      }}
    >
      <Typography variant="h4">Transcript</Typography>
      {phoneCall.state === PhoneCall_State.COMPLETED ? (
        <Box sx={{ display: "flex", gap: "8px" }}>
          <CopyTranscript
            phoneCall={phoneCall}
            trackToUser={trackToUser}
            options={transcriptCopyToOptions}
            isOpen={copyPopperOpen}
            setIsOpen={setCopyPopperOpen}
            onCopyToHandler={() => {
              setEntityCopyType("transcript");
            }}
          />
          <DownloadTranscript phoneCall={phoneCall} trackToUser={trackToUser} />
        </Box>
      ) : null}
    </Box>
  );
  return (
    <>
      <TwoPanelGrid
        defaultPanel={defaultPanel}
        showHeaderAction={!isMobile}
        leftPanel={{
          title: mainHeader,
          switchContent: "Call details",
          content: (
            <PhoneCallDetailsPanel
              familyRef={familyRef}
              suggestions={data.suggestions}
              callNotes={callNotes}
              recipient={recipients[0]}
              phoneCall={phoneCall}
              copyToOptions={noteCopyToOptions}
              onCopyToHandler={() => {
                setEntityCopyType("note");
              }}
              delegationTaskRef={delegationTaskRef}
            />
          ),
          sx: { overflow: "hidden" },
        }}
        rightPanel={{
          title: isMobile ? mainHeader : transcriptHeader,
          switchContent: "Transcript",
          content: (
            <PhoneCallActivityPanel
              phoneCall={phoneCall}
              trackToUser={trackToUser}
              segmentRefs={segmentRefs ? segmentRefs : undefined}
              refresh={() => request({ callRef: callRef })}
              familyRef={familyRef}
            />
          ),
        }}
      />
      <BookmarkToTaskDialog
        familyRef={copyToFamily?.ref || ""}
        familyName={copyToFamily?.name || ""}
        open={!!copyToFamily}
        onClose={() => {
          setCopyToFamily(undefined);
        }}
        entry={fromPhoneCallNotes(title)}
        onAction={onBookmarkHandler}
        singleSelection={true}
      />
    </>
  );
};
